"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { CategoryVideosSidebar } from "./CategoryVideosSidebar";
import { PostAuthors } from "@/views/post/partials/PostAuthors/PostAuthors";
import { PostBody } from "@/views/post/partials/PostBody/PostBody";
import { usePostData } from "@/views/post/context/PostDataContext";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { Post, Media, Category } from "@/payload-types";
import { getMediaUrl } from "@/utilities/media/getMediaUrl";
import { AdSlot } from "@/features/ads/AdSlot";
import type { LocaleCode } from "@/lib/labels/types";
import { VideoEmbed } from "../../partials/VideoEmbed/VideoEmbed";

/**
 * Format date for video display
 */
function formatDate(dateString: string | null | undefined, locale: LocaleCode): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

/**
 * Update page metadata (title, description, og tags)
 */
function updatePageMetadata(post: Post, tenantName: string) {
  // Update document title
  document.title = `${post.title} | ${tenantName}`;

  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", post.summary || post.title);
  }

  // Update Open Graph tags
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) {
    ogTitle.setAttribute("content", `${post.title} | ${tenantName}`);
  }
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) {
    ogDescription.setAttribute("content", post.summary || post.title);
  }
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) {
    ogUrl.setAttribute("content", window.location.href);
  }

  // Update hero image in og:image if available — use "og" pre-generated size
  const heroImage = post.heroImage as Media | null;
  const ogImageUrl = getMediaUrl(heroImage, "og") || heroImage?.url;
  if (ogImageUrl) {
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute("content", ogImageUrl);
    }
  }
}

/**
 * Push pageview event
 */
function pushPageview(post: Post, url: string) {
  // Google Analytics 4 (gtag)
  if (typeof window !== "undefined" && "gtag" in window) {
    (window as typeof window & {
      gtag: (...args: unknown[]) => void;
    }).gtag("event", "page_view", {
      page_title: post.title,
      page_location: url,
      page_path: new URL(url).pathname
    });
  }

  // Google Tag Manager dataLayer
  if (typeof window !== "undefined" && "dataLayer" in window) {
    (window as typeof window & {
      dataLayer: unknown[];
    }).dataLayer.push({
      event: "pageview",
      page: {
        title: post.title,
        url: url,
        path: new URL(url).pathname
      },
      video: {
        id: post.id,
        title: post.title,
        slug: post.slug
      }
    });
  }

  // Custom event for any other analytics
  window.dispatchEvent(new CustomEvent("video:pageview", {
    detail: {
      postId: post.id,
      title: post.title,
      slug: post.slug,
      url: url
    }
  }));
}

/**
 * Single Video Content Component - renders one full video post
 */
function VideoContent(t0) {
  const $ = _c(37);
  const {
    post,
    category,
    locale,
    isFirst: t1
  } = t0;
  const isFirst = t1 === undefined ? false : t1;
  const heroImage = post.heroImage as Media | null;
  const labels = useLabels();
  const TitleTag = isFirst ? "h1" : "h2";
  let t2;
  if ($[0] !== category) {
    t2 = category && <div className="mb-4"><span className="text-xs font-bold text-primary uppercase tracking-wider">{category.title}</span></div>;
    $[0] = category;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== TitleTag || $[3] !== post.title) {
    t3 = <TitleTag className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">{post.title}</TitleTag>;
    $[2] = TitleTag;
    $[3] = post.title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== post.populatedAuthors) {
    t4 = post.populatedAuthors && post.populatedAuthors.length > 0 && <><span className="font-medium text-foreground">{post.populatedAuthors.map(_temp).join(", ")}</span><span>·</span></>;
    $[5] = post.populatedAuthors;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = post.publishedAt || undefined;
  let t6;
  if ($[7] !== locale || $[8] !== post.publishedAt) {
    t6 = formatDate(post.publishedAt, locale);
    $[7] = locale;
    $[8] = post.publishedAt;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <time dateTime={t5}>{t6}</time>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t7) {
    t8 = <div className="flex flex-wrap items-center gap-2 text-sm text-foreground/60 mb-6">{t4}{t7}</div>;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== heroImage || $[17] !== isFirst || $[18] !== post.title || $[19] !== post.videoEmbed) {
    t9 = <VideoEmbed videoEmbed={post.videoEmbed} heroImage={heroImage} title={post.title} className="mb-8" heroSize="xlarge" priority={isFirst} />;
    $[16] = heroImage;
    $[17] = isFirst;
    $[18] = post.title;
    $[19] = post.videoEmbed;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== post.summary) {
    t10 = post.summary && <p className="text-lg md:text-xl text-foreground/80 leading-relaxed mb-8 font-medium">{post.summary}</p>;
    $[21] = post.summary;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== post.content) {
    t11 = <PostBody content={post.content} />;
    $[23] = post.content;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = (labels as any).videoPage?.author;
  const t13 = (labels as any).videoPage?.authors;
  let t14;
  if ($[25] !== post.populatedAuthors || $[26] !== t12 || $[27] !== t13) {
    t14 = <PostAuthors authors={post.populatedAuthors} className="mt-8 pt-6" authorLabel={t12} authorsLabel={t13} />;
    $[25] = post.populatedAuthors;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t14 || $[32] !== t2 || $[33] !== t3 || $[34] !== t8 || $[35] !== t9) {
    t15 = <div className="max-w-180 py-8 border-b border-border last:border-b-0">{t2}{t3}{t8}{t9}{t10}{t11}{t14}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t14;
    $[32] = t2;
    $[33] = t3;
    $[34] = t8;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
}
function _temp(author) {
  return author.name;
}
interface VideoLayoutScrollingProps {
  breadcrumbs?: React.ReactNode;
}
export function VideoLayoutScrolling(t0) {
  const $ = _c(55);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    breadcrumbs
  } = t1;
  const {
    post,
    categoryPosts,
    primaryCategory: category,
    locale: localeStr,
    tenantName
  } = usePostData();
  const locale = localeStr as LocaleCode;
  const nav = useNavigation();
  let t2;
  if ($[2] !== post) {
    t2 = [post];
    $[2] = post;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [videos, setVideos] = useState(t2);
  const [currentVideoId, setCurrentVideoId] = useState(post.id);
  const [hasMore, setHasMore] = useState(categoryPosts.length > 0);
  const loaderRef = useRef(null);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const videoRefs = useRef(t3);
  const contentContainerRef = useRef(null);
  let t4;
  if ($[5] !== post.id) {
    t4 = new Set([post.id]);
    $[5] = post.id;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const viewedVideos = useRef(t4);
  let t5;
  if ($[7] !== categoryPosts || $[8] !== hasMore || $[9] !== videos) {
    t5 = () => {
      if (!hasMore) {
        return;
      }
      const existingIds = new Set(videos.map(_temp2));
      const remainingVideos = categoryPosts.filter(p => !existingIds.has(p.id));
      if (remainingVideos.length > 0) {
        setVideos(prev => [...prev, remainingVideos[0]]);
        setHasMore(remainingVideos.length > 1);
      } else {
        setHasMore(false);
      }
    };
    $[7] = categoryPosts;
    $[8] = hasMore;
    $[9] = videos;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const loadNextVideo = t5;
  let t6;
  let t7;
  if ($[11] !== hasMore || $[12] !== loadNextVideo) {
    t6 = () => {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
          loadNextVideo();
        }
      }, {
        threshold: 0.1
      });
      const current = loaderRef.current;
      if (current) {
        observer.observe(current);
      }
      return () => {
        if (current) {
          observer.unobserve(current);
        }
      };
    };
    t7 = [loadNextVideo, hasMore];
    $[11] = hasMore;
    $[12] = loadNextVideo;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  const observerRef = useRef(null);
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = new Set();
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const observedElementsRef = useRef(t8);
  const videosRef = useRef(videos);
  const currentVideoIdRef = useRef(currentVideoId);
  let t10;
  let t9;
  if ($[16] !== videos) {
    t9 = () => {
      videosRef.current = videos;
    };
    t10 = [videos];
    $[16] = videos;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[19] !== currentVideoId) {
    t11 = () => {
      currentVideoIdRef.current = currentVideoId;
    };
    t12 = [currentVideoId];
    $[19] = currentVideoId;
    $[20] = t11;
    $[21] = t12;
  } else {
    t11 = $[20];
    t12 = $[21];
  }
  useEffect(t11, t12);
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = new Map();
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const visibilityMapRef = useRef(t13);
  let t14;
  let t15;
  if ($[23] !== nav || $[24] !== tenantName || $[25] !== videos) {
    t14 = () => {
      const container = contentContainerRef.current;
      if (!container) {
        return;
      }
      const handleIntersection = entries_0 => {
        for (const entry of entries_0) {
          const videoId = Number((entry.target as HTMLElement).getAttribute("data-video-id"));
          if (videoId) {
            if (entry.isIntersecting) {
              visibilityMapRef.current.set(videoId, entry.intersectionRatio);
            } else {
              visibilityMapRef.current.set(videoId, 0);
            }
          }
        }
        let bestVideoId = null;
        let maxRatio = 0;
        visibilityMapRef.current.forEach((ratio, videoId_0) => {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            bestVideoId = videoId_0;
          }
        });
        if (!bestVideoId || maxRatio < 0.1) {
          return;
        }
        if (bestVideoId === currentVideoIdRef.current) {
          return;
        }
        const video = videosRef.current.find(a_0 => a_0.id === bestVideoId);
        if (!video) {
          return;
        }
        const newUrl = nav.video(video.slug);
        window.history.pushState({
          videoId: bestVideoId
        }, "", newUrl);
        updatePageMetadata(video, tenantName);
        if (!viewedVideos.current.has(bestVideoId)) {
          viewedVideos.current.add(bestVideoId);
          pushPageview(video, window.location.origin + newUrl);
        }
        setCurrentVideoId(bestVideoId);
      };
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: container,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        rootMargin: "-10% 0px -10% 0px"
      });
      visibilityMapRef.current.clear();
      observedElementsRef.current.clear();
      videos.forEach(video_0 => {
        const element = videoRefs.current.get(video_0.id);
        if (element) {
          observerRef.current.observe(element);
          observedElementsRef.current.add(video_0.id);
        }
      });
      return () => {
        observerRef.current?.disconnect();
      };
    };
    t15 = [nav, tenantName, videos];
    $[23] = nav;
    $[24] = tenantName;
    $[25] = videos;
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[28] !== videos) {
    t16 = () => {
      const timer = setTimeout(() => {
        if (!observerRef.current) {
          return;
        }
        videos.forEach(video_1 => {
          if (!observedElementsRef.current.has(video_1.id)) {
            const element_0 = videoRefs.current.get(video_1.id);
            if (element_0) {
              observerRef.current.observe(element_0);
              observedElementsRef.current.add(video_1.id);
            }
          }
        });
      }, 100);
      return () => clearTimeout(timer);
    };
    t17 = [videos];
    $[28] = videos;
    $[29] = t16;
    $[30] = t17;
  } else {
    t16 = $[29];
    t17 = $[30];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      const handlePopState = event => {
        if (event.state?.videoId) {
          const videoElement = videoRefs.current.get(event.state.videoId);
          if (videoElement) {
            videoElement.scrollIntoView({
              behavior: "smooth"
            });
          }
        }
      };
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    };
    t19 = [];
    $[31] = t18;
    $[32] = t19;
  } else {
    t18 = $[31];
    t19 = $[32];
  }
  useEffect(t18, t19);
  let t20;
  if ($[33] !== categoryPosts || $[34] !== currentVideoId || $[35] !== post) {
    t20 = <CategoryVideosSidebar initialPost={post} categoryPosts={categoryPosts} activePostId={currentVideoId} />;
    $[33] = categoryPosts;
    $[34] = currentVideoId;
    $[35] = post;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <AdSlot id="content-top" />;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== category || $[39] !== locale || $[40] !== videos) {
    t22 = videos.map((video_2, index) => <article key={video_2.id} ref={el => {
      if (el) {
        videoRefs.current.set(video_2.id, el);
      }
    }} data-video-id={video_2.id}><VideoContent post={video_2} category={category} locale={locale} isFirst={index === 0} /></article>);
    $[38] = category;
    $[39] = locale;
    $[40] = videos;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== hasMore || $[43] !== videos.length) {
    t23 = !hasMore && videos.length > 1 && <div className="text-center text-foreground/40 text-sm">You're all caught up!</div>;
    $[42] = hasMore;
    $[43] = videos.length;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t23) {
    t24 = <div ref={loaderRef} className="py-8">{t23}</div>;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <AdSlot id="content-bottom" />;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== breadcrumbs || $[49] !== t22 || $[50] !== t24) {
    t26 = <div ref={contentContainerRef} className="flex-1 overflow-y-auto"><div className="max-w-4xl mx-auto px-4 md:px-8 space-y-8">{breadcrumbs}{t21}{t22}{t24}{t25}</div></div>;
    $[48] = breadcrumbs;
    $[49] = t22;
    $[50] = t24;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== t20 || $[53] !== t26) {
    t27 = <div className="h-[calc(100vh-4rem)] bg-background flex overflow-hidden">{t20}{t26}</div>;
    $[52] = t20;
    $[53] = t26;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  return t27;
}
function _temp2(a) {
  return a.id;
}